import {
  Accordion,
  FilterBuilder,
  FilterBuilderFieldsType,
  FilterBuilderFilterType,
  Form,
  useForm,
} from "@validereinc/common-components";
import { VariableChoiceAction } from "@validereinc/domain";
import { cloneDeep } from "es-toolkit/compat";
import { produce } from "immer";
import React, { useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useWorkflowGraphState } from "../../../../Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

const WorkflowConfigurationSingleVariableChoice = ({
  choice,
  onChange,
  index,
  fieldsDefinition,
}: {
  choice: VariableChoiceAction["choices"][0];
  onChange: (formValues: VariableChoiceAction["choices"][0]) => void;
  index: number;
  fieldsDefinition: FilterBuilderFieldsType;
}) => {
  const [condition, setCondition] = useState<FilterBuilderFilterType>(
    choice.condition ?? {}
  );
  const form = useForm({
    defaultValues: {
      condition: choice.condition ?? {},
    },
  });

  const handleValueChange = (filter: FilterBuilderFilterType) => {
    //clone to go around immutable errors
    const updatedChoice = cloneDeep(choice);
    updatedChoice.condition = {
      ...cloneDeep(filter),
    };
    const numberRegex = /^[0-9]+(\.[0-9]+)?$/;
    //parse numbers in that case that a $eq is used with a number input
    Object.entries(filter).forEach(([variableName, condition]) => {
      if (typeof condition === "string" && numberRegex.test(condition)) {
        (updatedChoice as Record<string, any>).condition[variableName] =
          Number.parseFloat(condition);
      }
      if (typeof condition === "object" && condition !== null) {
        Object.entries(condition).forEach(([operator, val]) => {
          if (
            operator === "$eq" &&
            typeof val === "string" &&
            numberRegex.test(val)
          ) {
            (updatedChoice as Record<string, any>).condition[variableName][
              operator
            ] = Number.parseFloat(val);
          }
        });
      }
    });
    onChange(updatedChoice);
  };

  useDeepCompareEffect(() => {
    handleValueChange(condition);
  }, [condition]);

  return (
    <Accordion defaultActiveKeys={["variable-choice-filter"]}>
      <Accordion.AccordionPanel
        dataKey="variable-choice-filter"
        title={`Path ${index + 1}`}
        variant="bordered"
      >
        <Form
          {...form}
          style={{ maxWidth: 432 }}
        >
          <FilterBuilder
            fields={fieldsDefinition}
            filter={condition}
            setFilter={setCondition}
            variant="vertical"
            onFilterChange={(newFilter) => {
              setCondition(newFilter);
              form.setValue("condition", newFilter, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        </Form>
      </Accordion.AccordionPanel>
    </Accordion>
  );
};

const WorkflowConfigurationVariableChoiceDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const { setVariableChoice, setVariableChoiceIsValid, getVariableChoice } =
    useWorkflowConfigurationFormStore();
  const { getAvailableStepVariables } = useWorkflowGraphState();

  const storeValues = getVariableChoice();
  const fieldsDefinition = useMemo(() => {
    const availableVariables = getAvailableStepVariables(stepId);
    const fields: FilterBuilderFieldsType = {};
    availableVariables.forEach((variable) => {
      fields[variable] = {
        data_type: "mixed",
        display_name: variable,
      };
    });
    return fields;
  }, [getAvailableStepVariables]);

  return (
    <>
      {(storeValues?.choices ?? []).map((choice, idx) => (
        <WorkflowConfigurationSingleVariableChoice
          index={idx}
          choice={choice}
          key={`choice-${idx}`}
          fieldsDefinition={fieldsDefinition}
          onChange={(formValues) => {
            if (storeValues) {
              const updatedStoreValues = produce(storeValues, (draft) => {
                draft.choices[idx] = formValues;
              });
              setVariableChoice(updatedStoreValues);
              setVariableChoiceIsValid(
                updatedStoreValues.choices.length > 1 &&
                  updatedStoreValues.choices.every((choice) => {
                    if (!choice.condition) return false;
                    if (typeof choice.condition === "object") {
                      if (choice.condition === null) return false;
                      if (Object.keys(choice.condition).length === 0)
                        return false;
                      for (const value of Object.values(choice.condition)) {
                        if (typeof value === "object" && "$in" in value) {
                          if (!Array.isArray(value.$in)) return false;
                          if (value.$in.length === 0) return false;
                        }
                      }
                    }
                    return true;
                  })
              );
            }
          }}
        />
      ))}
    </>
  );
};

export default WorkflowConfigurationVariableChoiceDetails;
